


import { useQuery } from '@apollo/client';
import { ReportQueries } from '../utils/graphql/queries/report';
import { generateClientContextWithHeaders } from '../utils/graphql/graphQLsupportFunction';
import { Report, UserTeamRole } from '../utils/graphql/generatedTS/graphql';
import { useAuthContext } from '../context/AuthContext';
import { UseGetDashboardReportListParams, UseGetDashboardReportListReturn } from './useGetDashboardReportListType';
import { useGetCurrentUserTeam } from './useGetCurrentUserTeam';
import { useEffect, useMemo, useState } from 'react';

const REPORT_TABLE_LENGTH = 10;

export const useGetDashboardReportList = ({idTeam, filter}: UseGetDashboardReportListParams): UseGetDashboardReportListReturn => {

  const { authenticatedIdUser, authToken } = useAuthContext();
  const currentUserTeam = useGetCurrentUserTeam();

  const [startIndex, setStartIndex] = useState<number>(0);

  const isAdmin = currentUserTeam?.role === UserTeamRole.Admin;
  const isUser = currentUserTeam?.role === UserTeamRole.User;

  const skip = !authenticatedIdUser || !idTeam || !currentUserTeam || (!isAdmin && !isUser);

  let query: any;
  let variables: any;

  if (isAdmin) {
    if (filter) {
      query = ReportQueries.GET_TEAM_REPORT_LIST_BY_FILTER;
      variables = {
        getTeamReportListByFilterInput: {
          idTeam: idTeam as string,
          ...filter,
          nextToken: undefined,
        },
      };
    } else {
      query = ReportQueries.GET_TEAM_REPORT_LIST;
      variables = {
        idTeam: idTeam as string,
        nextToken: undefined,
      };
    }
  } else if (isUser) {
    if (filter) {
      query = ReportQueries.GET_USER_REPORT_LIST_BY_FILTER;
      variables = {
        getUserReportListByFilterInput: {
          idTeam: idTeam as string,
          ...filter,
          nextToken: undefined,
        },
      };
    } else {
      query = ReportQueries.GET_USER_REPORT_LIST;
      variables = {
        idUser: authenticatedIdUser as string,
        idTeam: idTeam as string,
        nextToken: undefined,
      };
    }
  }

  const { data, loading, error, fetchMore } = useQuery(query ?? ReportQueries.GET_TEAM_REPORT_LIST, {
    variables: variables ?? {}, // Ensure variables are always an object
    fetchPolicy: 'network-only',
    context: generateClientContextWithHeaders(authToken),
    skip: skip || !query, // Prevent running query if undefined
    pollInterval: 5000,
  });


  // Reset startIndex when the filter changes
  useEffect(() => {
    setStartIndex(0);
  }, [JSON.stringify(filter)]);

  const getReportListData = useMemo(() => {
    if (isAdmin) {
      return filter ? data?.getTeamReportListByFilter : data?.getTeamReportList;
    } else if (isUser) {
      return filter ? data?.getUserReportListByFilter : data?.getUserReportList;
    }
    return undefined;
  }, [data, isAdmin, isUser, filter]);

  // console.log('reportListTeam', reportListTeam?.getTeamReportList);

  const reportList: Report[] = useMemo(() => {
    return getReportListData?.reportList || [];
  }, [getReportListData]);

  const nextToken = getReportListData?.nextToken;

  const hasMoreToLoad = useMemo(() => {
    if (startIndex < reportList.length - (reportList.length % REPORT_TABLE_LENGTH)) return true;
    else if (nextToken) return true;
    else return false;
  }, [startIndex, reportList, nextToken]);

  const getFetchMoreVariables = (nextToken: string | undefined) => {
    if (isAdmin) {
      if (filter) {
        return {
          getTeamReportListByFilterInput: {
            idTeam: idTeam as string,
            ...filter,
            nextToken,
          },
        };
      } else {
        return {
          idTeam: idTeam as string,
          nextToken,
        };
      }
    } else if (isUser) {
      if (filter) {
        return {
          getUserReportListByFilterInput: {
            idTeam: idTeam as string,
            ...filter,
            nextToken,
          },
        };
      } else {
        return {
          idUser: authenticatedIdUser as string,
          idTeam: idTeam as string,
          nextToken,
        };
      }
    }
    return {};
  };


  /**
   * Load the previous batch of reports
   */
  const loadPrevious = async () => {
    if (startIndex === 0) return;
    setStartIndex(startIndex - REPORT_TABLE_LENGTH);
  };

  /**
   * Load the next batch of reports
   */
  const loadNext = async () => {
    if (startIndex + REPORT_TABLE_LENGTH >= reportList.length) {
      if (nextToken) {
        const fetchMoreOptions = {
          variables: getFetchMoreVariables(nextToken),
        };
        // @ts-ignore
        await fetchMore(fetchMoreOptions);
      }
    }
    setStartIndex(startIndex + REPORT_TABLE_LENGTH);
  };

  return {
    reportList: reportList.slice(startIndex, startIndex + REPORT_TABLE_LENGTH),
    error,
    loading,
    loadPrevious,
    loadNext,
    canLoadPrevious: startIndex > 0,
    canLoadNext: hasMoreToLoad,
  };
}
